import { Component, inject } from '@angular/core';
import { BaseComponent } from '../base/base.component';
import { DataTableSchema, TableColumnSchema } from '../../model/dynamic-data-table.model';
import { DynamicFormArray, FormFieldConfig, Props } from '../../model/form.model';
import { EditActionFieldSchema, successFailureMsg, UserDetailsByWorkdaySearchConfig, UserDetailsDeleteSearchConfig, UserDetailsListSearchConfig, UserDetailsUpdateSearchConfig, UserRoleSettingsFilterSchema, UserRoleSettingsFormName, UserRoleSettingsProps, UserRoleSettingsTableSchema, UserRoleSettingsTitle, UserSettingsDeleteActionName, UserSettingsEditActionName } from '../../model/access-control.model';
import { PageContainerComponent } from '../../layout/page-container/page-container.component';
import { MatDialog } from '@angular/material/dialog';
import { CommonUtils } from '../../utils/common.utils';
import { CommonService } from '../../services/common.service';
import { SearchContainerComponent } from '../../layout/search-container/search-container.component';
import { API_STATUS, ApiEnvNames, ERROR_RESPONSE, SearchConfig } from '../../model/api.model';
import { ConfirmationDialogComponent } from '../../layout/confirmation-dialog/confirmation-dialog.component';
import { BUTTON_NAMES, ScreenNames, User_Roles } from '../../model/common.model';
import { DropdownOptions, FieldNames } from '../../model/form-fields.model';
import { FormService } from '../../services/form.service';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { LucideUserX, LucideUsers } from '@lucide/angular';

type callFor = 'List' | 'Create' | 'Edit' | 'Delete'
@Component({
  selector: 'access-control',
  standalone: true,
  imports: [PageContainerComponent],
  templateUrl: './access-control.component.html',
  styleUrl: './access-control.component.scss'
})
export class AccessControlComponent extends BaseComponent {

  protected override data: string = UserRoleSettingsTitle;
  protected override filterSchema: DynamicFormArray = structuredClone(UserRoleSettingsFilterSchema);
  protected editActionFieldsSchema: DynamicFormArray = structuredClone(EditActionFieldSchema);
  public override dataTableSchema: DataTableSchema = UserRoleSettingsTableSchema;
  protected override props: Props = UserRoleSettingsProps;
  public title: string = UserRoleSettingsTitle;
  public screenDisplayNames: any = {};
  public fullDataSource: any[] = [];
  public userRoleForm!: FormGroup;
  public currentForm!: FormGroup;
  private isSearchFieldResetInProgress = false;
  commonUtils = inject(CommonUtils);
  service = inject(CommonService);
  formService = inject(FormService);
  constructor(readonly dialog: MatDialog) {
    super();
  }
  override ngOnInit() {
    super.ngOnInit();
    this.configureWorkdaySearchAction();
    this.updateDisplayLabels()
    this.updateTableSchema();
    this.onLoadDataFetch();
    this.filterSchema.ButtonsArray?.forEach(button => {
      button['customEnableDisable'] = true;
    });
    this.formService.form$.subscribe(form => {
      const currentForm = form as FormGroup & { formName?: string };
      this.dynamicFieldValidation(form);
      if (currentForm.formName !== UserRoleSettingsFormName) { return; }
      if (!this.userRoleForm) {
        this.userRoleForm = form
      }
      this.currentForm = form;
      this.enforceCreateUserFieldState();
      const workdayControl = form.get(FieldNames['WorkDayId']);
      if (workdayControl) {
        workdayControl.valueChanges.subscribe(_value => {
          this.resetOtherSearchFields(FieldNames['WorkDayId']);
        });
      }
      const emailControl = form.get(FieldNames['Email']);
      if (emailControl) {
        emailControl.valueChanges.subscribe(_value => {
          this.resetOtherSearchFields(FieldNames['Email']);
        });
      }
    });
  }

  // Resets every field except the one being searched by, so Workday and Email searches stay mutually exclusive
  private resetOtherSearchFields(searchFieldName: string) {
    if (this.isSearchFieldResetInProgress) { return; }
    this.isSearchFieldResetInProgress = true;
    this.disableEnableButtons(true);
    this.filterSchema['FieldsArray'].forEach(f => {
      if (f.fieldName !== searchFieldName) {
        f['selectedValue'] = f['defaultValue'];
        this.currentForm?.get(f.fieldName)?.setValue(f['defaultValue'], { emitEvent: true });
      }
    });
    [FieldNames['UserAccessRoles'], FieldNames['UserAccessScreens']].forEach(fieldName => {
      const control = this.currentForm?.get(fieldName);
      if (control) {
        control.clearValidators();
        control.setErrors(null);
        control.updateValueAndValidity({ emitEvent: false });
        control.disable({ emitEvent: false });
      }
      this.disableEnableButtons(true);
      const field = this.filterSchema.FieldsArray.find(f => f.fieldName === fieldName);
      if (field) {
        field.disabled = true;
        field.required = false;
      }
    });
    this.enforceCreateUserFieldState(searchFieldName);
    this.disableEnableButtons(true);
    this.isSearchFieldResetInProgress = false;
  }

  private enforceCreateUserFieldState(activeSearchFieldName?: string): void {
    this.setCreateUserFieldDisabled(FieldNames['FirstName'], true);
    this.setCreateUserFieldDisabled(FieldNames['LastName'], true);

    const workdayId = this.currentForm?.get(FieldNames['WorkDayId'])?.value?.toString()?.trim();
    const email = this.currentForm?.get(FieldNames['Email'])?.value?.toString()?.trim();
    const hasWorkdayId = !!workdayId;
    const hasEmail = !!email;

    this.setCreateUserFieldDisabled(FieldNames['WorkDayId'], activeSearchFieldName === FieldNames['Email'] && hasEmail);
    this.setCreateUserFieldDisabled(FieldNames['Email'], activeSearchFieldName === FieldNames['WorkDayId'] && hasWorkdayId);
  }

  private setCreateUserFieldDisabled(fieldName: string, disabled: boolean): void {
    const field = this.filterSchema.FieldsArray.find(f => f.fieldName === fieldName);
    if (field) {
      field.disabled = disabled;
    }
    const control = this.currentForm?.get(fieldName);
    if (!control || control.disabled === disabled) {
      return;
    }
    if (disabled) {
      control.disable({ emitEvent: false });
    } else {
      control.enable({ emitEvent: false });
    }
  }

  configureWorkdaySearchAction() {
    const workdayField = this.filterSchema.FieldsArray.find(field => field.fieldName === FieldNames['WorkDayId']);
    if (workdayField) {
      workdayField.fieldActionConfig = {
        icon: 'search',
        click: (workdayId: string) => this.onWorkdaySearchClick(workdayId)
      }
    }
    const emailField = this.filterSchema.FieldsArray.find(field => field.fieldName === FieldNames['Email']);
    if (emailField) {
      emailField.fieldActionConfig = {
        icon: 'search',
        click: (email: string) => this.onEmailSearchClick(email)
      }
    }
  }
  onWorkdaySearchClick(workdayId: string) {
    const selectedWorkdayId = workdayId?.toString()?.trim();
    if (!selectedWorkdayId) {
      this.commonUtils.showMatSnackBar('Please enter Workday Id');
      return;
    }
    const isWorkdayExists = this.fullDataSource.find((user: any) => user[FieldNames['WorkDayId']] === selectedWorkdayId);
    if (isWorkdayExists) {
      this.commonUtils.showMatSnackBar('The user already has access to the application');
      this.setTableLoadingState(false);
      this.disableEnableButtons(true);
      return;
    }
    const searchParam: SearchConfig = structuredClone(UserDetailsByWorkdaySearchConfig);
    searchParam['body'][FieldNames['WorkDayId']] = selectedWorkdayId;
    this.setTableLoadingState(true);
    this.filterSchema['FieldsArray'].forEach(f => {
      if (f.fieldName !== FieldNames['WorkDayId']) {
        f['selectedValue'] = f['defaultValue']
      }
    });
    this.commonUtils.spinner('show');
    this.service.getDataFromDataSource(searchParam, ApiEnvNames['UserAccess']).then(response => {
      if (response['status'] === API_STATUS['Success'] && Array.isArray(response.result) && response.result.length > 0) {
        const userDetails = response.result[0];
        const mappedDetails: any = {
          [FieldNames['FirstName']]: userDetails['givenName'] ?? '',
          [FieldNames['LastName']]: userDetails['surname'] ?? '',
          [FieldNames['Email']]: userDetails['mail'] ?? ''
        };
        this.filterSchema.FieldsArray.forEach(field => {
          if (field.fieldName !== FieldNames['WorkDayId'] && mappedDetails[field.fieldName] !== undefined) {
            field.selectedValue = mappedDetails[field.fieldName];
            this.currentForm?.get(field.fieldName)?.setValue(mappedDetails[field.fieldName], { emitEvent: false });
            field.disabled = true;
            this.currentForm?.get(field.fieldName)?.disable({ emitEvent: false });
          }
        });
        this.commonUtils.spinner('hide');
        this.setTableLoadingState(false);
        setTimeout(() => {
          const roleField = this.filterSchema.FieldsArray.find(f => f.fieldName === FieldNames['UserAccessRoles']);
          if (roleField) {
            roleField.disabled = false;
            roleField.required = true;
          }
          const control = this.currentForm?.get(FieldNames['UserAccessRoles']);
            if (control) {
              control.enable({ emitEvent: false });
              control.setValidators(Validators.required);
              control.updateValueAndValidity({ emitEvent: false });
            }
          this.disableEnableButtons(true);
        });
      } else {
        this.commonUtils.spinner('hide');
        this.disableEnableButtons(true);
        this.filterSchema['FieldsArray'].forEach(f => {
          if (f.fieldName === FieldNames['WorkDayId']) {
            f['disabled'] = false;
          } else {
            f['selectedValue'] = f['defaultValue'];
            f['disabled'] = true;
            this.currentForm?.get(f.fieldName)?.disable({ emitEvent: false });
            }
        });
        const workdayFormControl = this.currentForm?.get(FieldNames['WorkDayId']);
        if (workdayFormControl) {
          workdayFormControl.enable({ emitEvent: false });
        }
        this.commonUtils.showMatSnackBar('User not found. Please verify the Workday ID');
      }
    }).catch(() => {
      this.commonUtils.spinner('hide');
      this.commonUtils.showMatSnackBar('Error fetching user details by Workday Id');
      this.setTableLoadingState(false);
    });
  }
  onEmailSearchClick(email: string) {
    const selectedEmail = email?.toString()?.trim();
    if (!selectedEmail) {
      this.commonUtils.showMatSnackBar('Please enter Email');
      return;
    }
    const isEmailExists = this.fullDataSource.find((user: any) => user[FieldNames['Email']] === selectedEmail);
    if (isEmailExists) {
      this.commonUtils.showMatSnackBar('The user already has access to the application');
      this.setTableLoadingState(false);
      this.disableEnableButtons(true);
      return;
    }
    const searchParam: SearchConfig = structuredClone(UserDetailsByWorkdaySearchConfig);
    searchParam['body'] = { email: selectedEmail };
    this.setTableLoadingState(true);
    this.filterSchema['FieldsArray'].forEach(f => {
      if (f.fieldName !== FieldNames['Email']) {
        f['selectedValue'] = f['defaultValue']
      }
    });
    this.commonUtils.spinner('show');
    this.service.getDataFromDataSource(searchParam, ApiEnvNames['UserAccess']).then(response => {
      if (response['status'] === API_STATUS['Success'] && Array.isArray(response.result) && response.result.length > 0) {
        const userDetails = response.result[0];
        const mappedDetails: any = {
          [FieldNames['FirstName']]: userDetails['givenName'] ?? '',
          [FieldNames['LastName']]: userDetails['surname'] ?? '',
          [FieldNames['WorkDayId']]: userDetails['employeeId'] ?? ''
        };
        this.filterSchema.FieldsArray.forEach(field => {
          if (field.fieldName !== FieldNames['Email'] && mappedDetails[field.fieldName] !== undefined) {
            field.selectedValue = mappedDetails[field.fieldName];
            this.currentForm?.get(field.fieldName)?.setValue(mappedDetails[field.fieldName], { emitEvent: false });
            field.disabled = true;
            this.currentForm?.get(field.fieldName)?.disable({ emitEvent: false });
          }
        });
        this.commonUtils.spinner('hide');
        this.setTableLoadingState(false);
        setTimeout(() => {
          const roleField = this.filterSchema.FieldsArray.find(f => f.fieldName === FieldNames['UserAccessRoles']);
          if (roleField) {
            roleField.disabled = false;
            roleField.required = true;
          }
          const control = this.currentForm?.get(FieldNames['UserAccessRoles']);
          if (control) {
            control.enable({ emitEvent: false });
            control.setValidators(Validators.required);
            control.updateValueAndValidity({ emitEvent: false });
          }
          this.disableEnableButtons(true);
        });
      } else {
        this.commonUtils.spinner('hide');
        this.disableEnableButtons(true);
        this.filterSchema['FieldsArray'].forEach(f => {
          if (f.fieldName === FieldNames['Email']) {
            f['disabled'] = false;
          } else {
            f['selectedValue'] = f['defaultValue'];
            f['disabled'] = true;
            this.currentForm?.get(f.fieldName)?.disable({ emitEvent: false });
          }
        });
        const emailFormControl = this.currentForm?.get(FieldNames['Email']);
        if (emailFormControl) {
          emailFormControl.enable({ emitEvent: false });
        }
        this.commonUtils.showMatSnackBar('No user found for the given Email');
      }
    }).catch(() => {
      this.commonUtils.spinner('hide');
      this.commonUtils.showMatSnackBar('Error fetching user details by Email');
      this.setTableLoadingState(false);
    });
  }
  updateDisplayLabels() {
    DropdownOptions[FieldNames['UserAccessScreens']].forEach(screen => {
      this.screenDisplayNames[screen['fieldValue']] = screen['fieldLabel']
    })
  }
  updateTableSchema() {
    this.dataTableSchema.columnSchema.forEach(columnElm => {
      if (columnElm?.fieldType === 'Actions') {
        const actions = columnElm?.actionConfig;
        if (Array.isArray(actions) && actions.length > 0) {
          actions.forEach(action => {
            if (action.fieldKey === UserSettingsEditActionName) {
              action.onClick = this.editActionOnClick
            } else if (action.fieldKey === UserSettingsDeleteActionName) {
              action.onClick = this.deleteActionOnClick
            }
          })
        }
      }
    })
  }
  fetchAndUpdateData(searchParam: SearchConfig, successFailureMsg: successFailureMsg, callFor: callFor) {
    this.setTableLoadingState(true);
    if (callFor !== 'List') {
      this.commonUtils.spinner('show');
    }
    this.service.getDataFromDataSource(searchParam, ApiEnvNames['UserAccess']).then(response => {
      if (response['status'] === API_STATUS['Success']) {
        if (Array.isArray(response.result)) {
          if (successFailureMsg[API_STATUS['Success']] !== '') {
            this.commonUtils.showMatSnackBar(successFailureMsg[API_STATUS['Success']])
          }
          if (callFor === 'List') {
            this.fullDataSource = response.result;
            this.dataTableSchema.dataSource = response.result.filter((record: any) => record.showInUI === true)
          } else {
            this.onLoadDataFetch()
          }
        }
      } else {
        if (callFor === 'List') {
          this.dataTableSchema.dataSource = [];
        }
        this.commonUtils.showMatSnackBar(response['errorMsg'] ?? successFailureMsg[API_STATUS['Error']])
      }

      this.setTableLoadingState(false);
      if (callFor !== 'List') {
        this.commonUtils.spinner('hide');
      }
    }).catch(err => {
      if (callFor === 'List') {
        this.dataTableSchema.dataSource = [];
      }
      console.info(ERROR_RESPONSE['layoutErrorPrefix'], err);
      this.commonUtils.showMatSnackBar(successFailureMsg[API_STATUS['Error']])
      this.setTableLoadingState(false);
      if (callFor !== 'List') {
        this.commonUtils.spinner('hide');
      }
    })
  }
  onLoadDataFetch() {
    this.dataTableSchema.inputFilter = '';
    const searchParam: SearchConfig = structuredClone(UserDetailsListSearchConfig);
    const successFailureMsg: successFailureMsg = {
      [API_STATUS['Success']]: '',
      [API_STATUS['Error']]: 'Error On Fetching User Details'
    }
    this.fetchAndUpdateData(searchParam, successFailureMsg, 'List');
  };
  override  onSearchClicked = (searchParam: SearchConfig) => {
    this.userRoleForm.markAllAsTouched();
    this.currentForm.markAllAsTouched();
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      disableClose: true,
      panelClass: 'access-control-confirmation-dialog',
      data: {
        message: "This will create new user record",
        header: "Confirm User Info Creation",
        icon: LucideUsers
      }
    });
    dialogRef.afterClosed().subscribe(result => {
      if (result) {
        const payload: any = {};
        this.filterSchema['FieldsArray'].forEach(field => {
          if (field['selectedValue'] && field['selectedValue'] !== null && field['selectedValue'] !== '') {
            payload[field['fieldName']] = field['selectedValue']
          }
        });
        const successFailureMsg: successFailureMsg = {
          [API_STATUS['Success']]: 'User Details Created Sucessfully',
          [API_STATUS['Error']]: 'Error On Creating User Details'
        }
        if (this.service.loggedUserDetails) {
          payload['createdBy'] = this.service.loggedUserDetails['workdayId'];
          payload['createdByUser'] = this.service.loggedUserDetails['userName'];
          payload['updatedBy'] = this.service.loggedUserDetails['workdayId'];
          payload['updatedByUser'] = this.service.loggedUserDetails['userName']
        }
        searchParam['body'] = payload;
        this.onResetClicked();
        this.fetchAndUpdateData(searchParam, successFailureMsg, 'Create');
      }
      dialogRef.close()
    });
  };
  override onResetClicked = () => {
    this.userRoleForm?.reset();
    const roleControl = this.currentForm?.get(FieldNames['UserAccessRoles']);
    if (roleControl) {
      roleControl.clearValidators();
      roleControl.setErrors(null);
      roleControl.updateValueAndValidity({ emitEvent: false });
      roleControl.disable({ emitEvent: false });
    }

    const screenControl = this.currentForm?.get(FieldNames['UserAccessScreens']);
    if (screenControl) {
      screenControl.setValue([], { emitEvent: false });
      screenControl.clearValidators();
      screenControl.setErrors(null);
      screenControl.updateValueAndValidity({ emitEvent: false });
      screenControl.disable({ emitEvent: false });
    }

    this.disableEnableButtons(true);
    this.filterSchema['FieldsArray'].forEach(field => {
      if (field['fieldName'] === FieldNames['UserAccessRoles'] || field.fieldName === FieldNames['UserAccessScreens']) {
        field['selectedValue'] = field['defaultValue'];
        field['disabled'] = true;
      } else {
        field['selectedValue'] = field['defaultValue'];
      }
    });
  };
  editActionOnClick = (record: any) => {
    if (!this.service.loggedUserDetails) {
      this.commonUtils.showMatSnackBar("User-info data missing, Please try again after sometime");
      return
    }
    if (record[FieldNames['WorkDayId']] === this.service.loggedUserDetails['workdayId']) {
      this.commonUtils.showMatSnackBar("Logged user record can't be modified");
      return
    }
    const dialogRef = this.dialog.open(SearchContainerComponent, {
      panelClass: 'user-settings-edit-dialog'
    });
    const instance = dialogRef.componentInstance;
    instance.dynamicFormArray = structuredClone(this.editActionFieldsSchema);
    instance.dynamicFormArray.isCloseIcon = true;
    instance.closeDialog.subscribe(() => dialogRef.close());
    instance.dynamicFormArray.FieldsArray.forEach(field => {
      field['disabled'] = false;
      field['selectedValue'] = record[field['fieldName']]
      if (field['fieldName'] === FieldNames['UserAccessScreens']) {
        const selectedUserRole = record[FieldNames['UserAccessRoles']]
        field['disabled'] = false;
        field['required'] = true;
        if (selectedUserRole === User_Roles.Admin || selectedUserRole === User_Roles.Super_Admin) {
          field['disabled'] = true;
          field['required'] = false;
          field['selectedValue'] = Object.values(ScreenNames);
        }
      }
    })
    instance.dynamicFormArray.ButtonsArray?.forEach(action => {
      action.actionCallback = (actionInfo: any) => {
        if (actionInfo['name'] === BUTTON_NAMES.Modify) {
          let isDialogFormValid = true;
          instance.dynamicFormArray.FieldsArray.forEach(field => {
            if (field.required && (!field.selectedValue || (Array.isArray(field.selectedValue) && field.selectedValue.length === 0))) {
              isDialogFormValid = false;
            }
          });
          if (!isDialogFormValid) {
            this.commonUtils.showMatSnackBar("Please fill all required fields");
            return;
          }
          instance.dynamicFormArray.FieldsArray.forEach(field => {
            record[field['fieldName']] = field['selectedValue']
          })
          const searchParam: SearchConfig = structuredClone(UserDetailsUpdateSearchConfig);
          if (record) {
            searchParam['body'] = structuredClone(record);
            searchParam['body']['update'] = true;
            if (!record['createdByUser']) {
              searchParam['body']['createdByUser'] = "null";
            }
            if (this.service.loggedUserDetails) {
              searchParam['body']['updatedBy'] = this.service.loggedUserDetails['workdayId'];
              searchParam['body']['updatedByUser'] = this.service.loggedUserDetails['userName'];
            }
            const successFailureMsg: successFailureMsg = {
              [API_STATUS['Success']]: 'User Details Modified Sucessfully',
              [API_STATUS['Error']]: 'Error On Modifiying User Details'
            }
              this.dataTableSchema = {
              ...this.dataTableSchema,
              dataSource: []
            };
            this.fetchAndUpdateData(searchParam, successFailureMsg, 'Edit');
            this.disableEnableButtons(true);
          }
        }
        dialogRef.close()
      }
    })
  }
  deleteActionOnClick = (record: any, _column: TableColumnSchema) => {
    if (!this.service.loggedUserDetails) {
      this.commonUtils.showMatSnackBar("User-info data missing, Please try again after sometime");
      return
    }
    if (record[FieldNames['WorkDayId']] === this.service.loggedUserDetails['workdayId']) {
      this.commonUtils.showMatSnackBar("Logged user record can't be deleted");
      return
    }
    const dialogRef = this.dialog.open(ConfirmationDialogComponent, {
      disableClose: true,
      panelClass: 'access-control-confirmation-dialog',
      data: {
        message: "Access for the selected user will be removed, Are your sure?",
        header: "Remove Access",
        icon: LucideUserX
      }
    });
    dialogRef.afterClosed().subscribe(onConfirm => {
      if (onConfirm) {
        const successFailureMsg: successFailureMsg = {
          [API_STATUS['Success']]: 'User Details Deleted Sucessfully',
          [API_STATUS['Error']]: 'Error On Deleting User Details'
        }
        const searchParam: SearchConfig = structuredClone(UserDetailsDeleteSearchConfig);
        if (this.service.loggedUserDetails) {
          searchParam['body']['updatedBy'] = this.service.loggedUserDetails['workdayId'];
        }
        this.dataTableSchema = {
            ...this.dataTableSchema,
            dataSource: []
        };
        searchParam['body'][FieldNames['WorkDayId']] = record[FieldNames['WorkDayId']];
        this.fetchAndUpdateData(searchParam, successFailureMsg, 'Delete');
      }
      dialogRef.close()
    });
  }
  dynamicFieldValidation(form: any) {
    const accessScreenControl: FormControl = form.get(FieldNames.UserAccessScreens);
    const roleControl = form.get(FieldNames.UserAccessRoles);
    const isMainForm = form['formName'] === UserRoleSettingsFormName;
    form.get(FieldNames.UserAccessRoles)?.valueChanges.subscribe((value: string) => {
      const fieldArray = form['fieldArray'] as FormFieldConfig[];
      const screen = fieldArray?.find((x: { fieldName: string; }) => x.fieldName === FieldNames['UserAccessScreens'])
      if (accessScreenControl && screen) {
        if (value === User_Roles.Super_Admin || value === User_Roles.Admin || value === '') {
          screen.disabled = true;
          screen.required = false;
        }
        if (value === User_Roles.Super_Admin || value === User_Roles.Admin) {
          screen.selectedValue = Object.values(ScreenNames);
          accessScreenControl.setValue(Object.values(ScreenNames), { emitEvent: false });
          accessScreenControl.enable({ emitEvent: false })
          accessScreenControl.clearValidators()
        }
        else if (value === User_Roles.User) {
          screen.disabled = false;
          screen.required = true;
          screen.selectedValue = [];
          accessScreenControl.setValue([], { emitEvent: false });
          accessScreenControl.setValidators(Validators.required);
          accessScreenControl.enable({ emitEvent: false });
        }
        else {
          screen.selectedValue = [];
          screen.disabled = true;
          accessScreenControl.setValue([], { emitEvent: false });
          accessScreenControl.clearValidators();
          accessScreenControl.disable({ emitEvent: false })
        }
        accessScreenControl.updateValueAndValidity();
      }
      this.checkForAnyEmptyFields();
    if (isMainForm) {
      const currentRoleValue = form.get(FieldNames['UserAccessRoles'])?.value;
      const currentScreenValues = form.get(FieldNames['UserAccessScreens'])?.value;
      if (currentRoleValue && currentRoleValue !== '' &&
          Array.isArray(currentScreenValues) && currentScreenValues.length > 0) {
        this.disableEnableButtons(false);
      } else {
        this.disableEnableButtons(true);
      }
      }
    });
    form.get(FieldNames.UserAccessScreens)?.valueChanges.subscribe((_value: any) => {
      accessScreenControl.markAsTouched();
      roleControl?.markAsTouched();
      if (isMainForm) {
      const currentRoleValue = form.get(FieldNames['UserAccessRoles'])?.value;
      const currentScreenValues = form.get(FieldNames['UserAccessScreens'])?.value;
      if (currentRoleValue && currentRoleValue !== '' &&
          Array.isArray(currentScreenValues) && currentScreenValues.length > 0) {
        this.disableEnableButtons(false);
      } else {
        this.disableEnableButtons(true);
      }
      }
    });
  }

  override  checkForAnyEmptyFields = () => { }
}